import React, { useCallback, useState } from "react";
import AiFeatureToolPrep from "../../shared/Popup/AiFeatureToolPrep";
import AiMaskPaintPreview from "../../shared/Popup/AiMaskPaintPreview";
import AiMaskSelectToolControls from "../../shared/Popup/AiMaskSelectToolControls";
import { AI_MASK_PAINT_TOOLS } from "../../shared/Popup/AiSelectToolCards";
import { useAiMaskPaint } from "../../shared/Popup/useAiMaskPaint";
import CustomColorPicker from "../../../../../components/shared/CustomColorPicker";
import { recolorSingle } from "../../../../../services/api/tools/recolor";
import { resolveMaskEditSource } from "../../shared/Popup/maskFollowUpSource";
import "./RecolorToolPrep.css";

const COLOR_SWATCHES = [
  { id: "red", hex: "#E53935", label: "Red" },
  { id: "blue", hex: "#1E88E5", label: "Blue" },
  { id: "green", hex: "#43A047", label: "Green" },
  { id: "yellow", hex: "#FDD835", label: "Yellow" },
  { id: "purple", hex: "#8E24AA", label: "Purple" },
  { id: "orange", hex: "#FB8C00", label: "Orange" },
  { id: "pink", hex: "#EC407A", label: "Pink" },
  { id: "white", hex: "#FFFFFF", label: "White" },
  { id: "black", hex: "#212121", label: "Black" },
];

const PAINT_TOOLS = new Set(["brush", "box", "eraser"]);

function swatchFill(swatch) {
  return `${swatch.label} ${swatch.hex}`;
}

function hexInText(value) {
  const match = String(value || "").match(/#([0-9A-Fa-f]{6})\b/);
  return match ? `#${match[1].toUpperCase()}` : null;
}

function swatchForHex(hex) {
  if (!hex) return null;
  const key = hex.toUpperCase();
  return COLOR_SWATCHES.find((swatch) => swatch.hex.toUpperCase() === key) || null;
}

/**
 * AI Tools → Recolor prep.
 * What to recolor is a short object name, or a brush / box / eraser mask.
 * Recolor with is a color description. A swatch fills that box with the
 * color name and code, and the box stays editable.
 */
const RecolorToolPrep = (props) => {
  const [target, setTarget] = useState("");
  const [colorText, setColorText] = useState("");
  const [color, setColor] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const paint = useAiMaskPaint(null);
  const toolId = paint.paintTool;
  const promptChosen = Boolean(target.trim());
  const maskChosen = PAINT_TOOLS.has(toolId);
  const hasMask = maskChosen && paint.hasMask;
  const hasArea = promptChosen || hasMask;
  const hasLook = Boolean(colorText.trim()) || Boolean(color);

  const isGenerateReady = useCallback(() => {
    return hasArea && hasLook;
  }, [hasArea, hasLook]);

  const handleGenerate = useCallback(
    async ({ token, file, image_url, resultImageUrl, stageKey }) => {
      const objectName = promptChosen ? target.trim() : "";
      const look = colorText.trim();
      const chosenHex = hexInText(look) || (color ? color.toUpperCase() : null);
      const chosenSwatch = swatchForHex(chosenHex);
      let colorPrompt = look;
      if (chosenHex) {
        colorPrompt = look
          .replace(new RegExp(chosenHex, "i"), "")
          .replace(/\s+/g, " ")
          .trim();
        if (
          chosenSwatch &&
          colorPrompt.toLowerCase() === chosenSwatch.label.toLowerCase()
        ) {
          colorPrompt = "";
        }
      }
      let mask = null;
      if (maskChosen && !promptChosen && paint.hasMask) {
        const blob = await paint.maskRef.current?.exportMaskBlob?.();
        if (blob) {
          mask = new File([blob], "mask.png", { type: "image/png" });
        }
      }
      if (!objectName && !mask) {
        throw new Error(
          maskChosen
            ? "Paint the area to recolor."
            : "Name what to recolor, or paint a mask."
        );
      }
      if (!colorPrompt && !chosenHex) {
        throw new Error("Type a color, or pick a swatch.");
      }

      const input = await resolveMaskEditSource({
        token,
        file,
        image_url,
        resultImageUrl,
        stageKey,
        hasMask: Boolean(mask),
        filename: "recolor-input.png",
      });

      return recolorSingle(token, input.file, {
        output_format: "png",
        ...(mask ? { mask } : objectName ? { target: objectName } : {}),
        ...(colorPrompt ? { prompt: colorPrompt } : {}),
        ...(chosenHex ? { color: chosenHex } : {}),
        ...(input.image_url ? { image_url: input.image_url } : {}),
      });
    },
    [
      color,
      colorText,
      maskChosen,
      paint.hasMask,
      paint.maskRef,
      promptChosen,
      target,
    ]
  );

  const selectPaintTool = (id) => {
    if (promptChosen) return;
    if (paint.paintTool === id) {
      paint.clearMask();
      paint.setPaintTool(null);
      return;
    }
    setTarget("");
    paint.setPaintTool(id);
  };

  return (
    <AiFeatureToolPrep
      {...props}
      onGenerate={handleGenerate}
      generateFileName="recolor-input.png"
      downloadFallbackName="recolor.png"
      toolKey="ai_recolor"
      title="AI Recolor"
      aka="Object Recolor"
      bio="Recolor objects and regions with AI color and prompts."
      howToSteps={[
        "Upload an image",
        "Name what to recolor, or paint it with Brush, Box, or Eraser",
        "Type a color or pick a swatch, then tap Generate",
      ]}
      howToTip="Name the object, such as “the cat”, or paint it. Type a color such as “violet”, or pick a swatch. A swatch fills Recolor with with the color name and code, and you can still edit that text."
      emptyPreviewLabel="Upload an image to preview recolor"
      loadingLabel="Generating…"
      emptyFooterHint="Name what to recolor or paint it, then type a color or pick a swatch."
      isGenerateReady={isGenerateReady}
      renderPreview={({
        imageUrl,
        loading,
        loadingLabel,
        emptyLabel,
      }) => (
        <AiMaskPaintPreview
          ref={paint.maskRef}
          imageUrl={imageUrl}
          loading={loading}
          loadingLabel={loadingLabel}
          emptyLabel={emptyLabel}
          tool={toolId}
          brushSize={paint.brushSize}
          paintEnabled={Boolean(imageUrl) && !loading && maskChosen && !promptChosen}
          clearVersion={paint.clearVersion}
          onMaskChange={paint.handleMaskChange}
        />
      )}
      renderControls={({ busy, leftPanelRef, sourceImageUrl }) => {
        const colorKey = color ? color.toUpperCase() : "";
        const isPresetColor = COLOR_SWATCHES.some(
          (s) => s.hex.toUpperCase() === colorKey
        );
        const customSelected = Boolean(colorKey) && !isPresetColor;

        return (
        <>
          <div className="aftp-section">
            <label className="aftp-label" htmlFor="aftp-recolor-target">
              What to recolor
            </label>
            <textarea
              id="aftp-recolor-target"
              className="aftp-input"
              rows={2}
              value={target}
              onChange={(e) => {
                const next = e.target.value;
                setTarget(next);
                if (next.trim()) {
                  paint.clearMask();
                  paint.setPaintTool(null);
                }
              }}
              placeholder="the cat"
              disabled={busy || maskChosen}
            />
            <p className="rtp-or">or</p>
          </div>

          <AiMaskSelectToolControls
            paint={{ ...paint, setPaintTool: selectPaintTool }}
            busy={busy}
            disabled={promptChosen}
            sourceImageUrl={sourceImageUrl}
            leftPanelRef={leftPanelRef}
            subject="area to recolor"
            tools={AI_MASK_PAINT_TOOLS}
          />

          <div className="aftp-section" id="recolor-color-section">
            <label className="aftp-label" htmlFor="aftp-recolor-color-text">
              Recolor with
            </label>
            <input
              id="aftp-recolor-color-text"
              className="aftp-input"
              value={colorText}
              onChange={(e) => {
                const next = e.target.value;
                setColorText(next);
                setColor(hexInText(next));
              }}
              placeholder="black, violet, navy"
              disabled={busy}
            />
            <div
              className="rtp-swatches"
              id="recolor-color-grid"
              role="listbox"
              aria-label="Color"
            >
              {COLOR_SWATCHES.map((swatch) => {
                const selected = colorKey === swatch.hex.toUpperCase();
                return (
                  <button
                    key={swatch.id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    aria-label={swatch.label}
                    title={swatch.label}
                    className={`rtp-swatch${selected ? " is-active" : ""}`}
                    style={{ background: swatch.hex }}
                    disabled={busy}
                    onClick={() => {
                      setColor(swatch.hex);
                      setColorText(swatchFill(swatch));
                      setShowColorPicker(false);
                    }}
                  />
                );
              })}
              <button
                type="button"
                role="option"
                aria-selected={customSelected}
                aria-label="Custom color"
                title="Custom color"
                id="recolor-color-picker"
                className={`rtp-swatch rtp-swatch--picker${
                  customSelected ? " is-active" : ""
                }`}
                disabled={busy}
                onClick={() => setShowColorPicker((prev) => !prev)}
              >
                {customSelected ? (
                  <span
                    className="rtp-swatch--picker-fill"
                    style={{ background: color }}
                    aria-hidden="true"
                  />
                ) : null}
              </button>
            </div>
          </div>

          <CustomColorPicker
            isOpen={showColorPicker}
            onClose={() => setShowColorPicker(false)}
            color={color || "#ffffff"}
            onApply={(nextColor) => {
              const hex = hexInText(nextColor) || String(nextColor || "").trim();
              if (!hex) return;
              const preset = swatchForHex(hex);
              setColor(preset ? preset.hex : hex);
              setColorText(preset ? swatchFill(preset) : hex);
            }}
            ignoreCloseSelectors={[
              "#recolor-color-picker",
              "#recolor-color-section",
            ]}
            anchorSelector='.tool-prep-popup[data-tool="ai_recolor"] .tool-prep-popup__left'
            anchorTopSelector="#recolor-color-grid"
            anchorGap={10}
          />
        </>
        );
      }}
    />
  );
};

export default RecolorToolPrep;
